import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import { styled } from '@mui/material/styles';
import UndoRounded from '@mui/icons-material/UndoRounded';
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import MoreHorizRounded from '@mui/icons-material/MoreHorizRounded';
import AccessTimeRounded from '@mui/icons-material/AccessTimeRounded';
import { Composer, Comment, type CommentBodyMentionProps, type ComposerEditorMentionProps } from '@liveblocks/react-ui/primitives';
import {
  THREAD_CONTAINER_Z_INDEX,
  THREAD_CONTAINER_ACTIVE_Z_INDEX,
  DELETE_OVERLAY_Z_INDEX,
  THREAD_INPUT_Z_INDEX,
  Z_INDEX_LEVEL3,
  THREAD_INPUT_MENTION_SUGGESTIONS_Z_INDEX,
} from './constants';
import type React from 'react';
import { extractNameFromMentionId } from './utils/mentionUtils';

export const PyThreadManagerContainer = styled(Box)({
  position: 'relative',
});

export const PyThreadPanelContainer = styled(Box)({
  width: 375,
  marginTop: 10,
  position: 'relative',
  marginRight: 80,
  zIndex: Z_INDEX_LEVEL3,
  opacity: 1,
  transform: 'translateX(0)',
  transition: 'opacity 350ms ease-out, transform 350ms ease-out',
  animation: 'fadeInSlide 350ms ease-out',
  '@keyframes fadeInSlide': {
    '0%': {
      opacity: 0,
      transform: 'translateX(20px)',
    },
    '100%': {
      opacity: 1,
      transform: 'translateX(0)',
    },
  },
  '@media (max-width: 1400px)': {
    position: 'absolute',
    right: 0,
    width: 375,
    pointerEvents: 'none',
    '& > *': {
      pointerEvents: 'auto',
    },
  },
});

export const PyThreadPanelHeaderContainer = styled(Box)({
  display: 'flex',
  padding: '12px 16px',
  justifyContent: 'space-between',
  alignItems: 'center',
  borderRadius: '24px',
  boxShadow: '0 2px 1px -1px rgba(0, 0, 0, 0.20), 0 1px 1px 0 rgba(0, 0, 0, 0.14), 0 1px 3px 0 rgba(0, 0, 0, 0.12)',
  backgroundColor: 'white',
  zIndex: THREAD_CONTAINER_Z_INDEX,
  position: 'fixed',
});

export const PyThreadPanelHeader = styled(Typography)(({ theme }) => ({
  fontSize: 20,
  color: theme.palette.text.primary,
  fontWeight: 600,
}));

export const PyThreadPanelSegmentedButtonContainer = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  borderRadius: '100px',
});

export const PyThreadPanelSegmentedButton = styled(Button)<{ isActive?: boolean }>(({ theme, isActive }) => ({
  display: 'flex',
  width: '110px',
  height: '40px',
  justifyContent: 'center',
  alignItems: 'center',
  alignSelf: 'stretch',
  backgroundColor: isActive ? theme.palette.primary.main : 'transparent',
  border: '1px solid #00283A80',
  color: isActive ? 'white' : theme.palette.text.primary,
  fontSize: 14,
  fontWeight: 600,
  boxShadow: isActive ? '0 2px 4px -1px rgba(0, 0, 0, 0.20), 0 4px 5px 0 rgba(0, 0, 0, 0.14), 0 1px 10px 0 rgba(0, 0, 0, 0.12)' : 'none',
  borderRadius: 0,
  '&:hover': {
    backgroundColor: theme.palette.primary.dark,
    color: 'white',
  },
}));

export const PyThreadContainer = styled(Box)<{
  isActive: boolean;
  top?: number;
  isShown?: boolean;
}>(({ isActive, top, isShown }) => ({
  position: 'absolute',
  left: 0,
  right: 0,
  top: `${top || 0}px`,
  zIndex: isActive ? THREAD_CONTAINER_ACTIVE_Z_INDEX : THREAD_CONTAINER_Z_INDEX,
  marginTop: 0,
  display: isShown ? 'flex' : 'none',

  padding: '0 16px',
  flexDirection: 'column',
  alignItems: 'flex-start',

  borderRadius: '24px',
  background: 'white',

  maxHeight: '400px',
  overflowY: 'auto',

  boxShadow: isActive
    ? '0 2px 4px -1px rgba(22, 19, 19, 0.2), 0 4px 5px 0 rgba(0, 0, 0, 0.14), 0 1px 10px 0 rgba(0, 0, 0, 0.12)'
    : '0 2px 1px -1px rgba(0, 0, 0, 0.20), 0 1px 1px 0 rgba(0, 0, 0, 0.14), 0 1px 3px 0 rgba(0, 0, 0, 0.12)',
}));

export const PyThreadHeader = styled(Box)({
  height: '60px',
  cursor: 'pointer',
  width: '100%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: '12px 0',
});

export const PyThreadHeaderTitle = styled(Typography)(({ theme }) => ({
  fontSize: 14,
  fontWeight: 600,
  color: theme.palette.primary.main,
  textWrap: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
}));

export const PyThreadHeaderCommentsCount = styled(Typography)(({ theme }) => ({
  width: '24px',
  height: '24px',
  borderRadius: '100px',
  backgroundColor: theme.palette.action.selected,
  color: theme.palette.text.primary,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontSize: 13,
}));

export const PyThreadInputMenuContainer = styled(Box)<{ top: number; left: number }>(({ top, left }) => ({
  position: 'absolute',
  top: `${top}px`,
  left: `${left}px`,
  zIndex: THREAD_INPUT_Z_INDEX,
  width: '100%',
  maxWidth: '500px',
  minWidth: '360px',
}));

export const PyThreadInputContainer = styled(Box)(() => ({
  width: 375,

  padding: '16px',

  borderRadius: '24px',
  background: 'white',

  boxShadow: '0 2px 4px -1px rgba(22, 19, 19, 0.2), 0 4px 5px 0 rgba(0, 0, 0, 0.14), 0 1px 10px 0 rgba(0, 0, 0, 0.12)',
}));

export const PyThreadInputHeader = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
});

export const PyThreadAuthorLabel = styled(Typography)(({ theme }) => ({
  fontSize: '14px',
  color: theme.palette.text.primary,
  fontWeight: 600,
}));

export const PyCheckboxContainer = styled(Box)({
  display: 'flex',
  alignItems: 'center',
});

export const PyCheckboxLabel = styled(Typography)(({ theme }) => ({
  fontSize: '14px',
  color: theme.palette.text.primary,
}));

export const PyThreadEditor = styled(Composer.Editor)(({ theme }) => ({
  width: '100%',
  borderRadius: '8px',
  padding: '8px 16px',
  marginBottom: '12px',
  background: 'white',
  border: `1px solid ${theme.palette.action.disabledBackground}`,
  position: 'relative',
  fontFamily: 'Gellix',
  outline: 'none',
  '&:focus': {
    outline: 'none',
  },
  '&:focus-visible': {
    outline: 'none',
  },
  '& p': {
    marginBottom: 0,
  },
}));

export const PyThreadInputActions = styled(Box)({
  display: 'flex',
  gap: 8,
  justifyContent: 'flex-end',
});

export const PySubmitButton = styled(Composer.Submit)(({ theme }) => ({
  padding: '4px 10px',
  borderRadius: '100px',
  fontSize: 13,
  fontWeight: 600,
  backgroundColor: theme.palette.primary.main,
  color: theme.palette.primary.contrastText,
  border: 'none',
  textTransform: 'none',
  '&:hover': {
    backgroundColor: theme.palette.primary.dark,
  },
  '&:disabled': {
    backgroundColor: theme.palette.action.disabledBackground,
    cursor: 'not-allowed',
  },
}));

export const PyCancelButton = styled(Button)(({ theme }) => ({
  padding: '4px 10px',
  borderRadius: '100px',
  fontSize: 13,
  fontWeight: 600,
  backgroundColor: 'white',
  color: theme.palette.primary.main,
  border: 'none',
  textTransform: 'none',
}));

export const PyResolveButton = styled(Button)(({ theme }) => ({
  padding: '0px 10px',
  borderRadius: 100,
  fontSize: 13,
  backgroundColor: theme.palette.primary.main,
  color: theme.palette.primary.contrastText,
  textTransform: 'none',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  '&:hover': {
    backgroundColor: theme.palette.primary.dark,
  },
}));

export const PyCommentContainer = styled(Box)(() => ({
  width: '100%',
  position: 'relative',
}));

export const PyCommentHeader = styled(Box)({
  display: 'flex',
  width: '100%',
  justifyContent: 'space-between',
  alignItems: 'center',
});

export const PyCommentAuthor = styled(Typography)(({ theme }) => ({
  fontSize: 16,
  fontWeight: 600,
  color: theme.palette.text.primary,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  maxWidth: '260px',
}));

export const PyPrivateLabel = styled(Typography)(({ theme }) => ({
  fontSize: 12,
  fontWeight: 400,
  color: theme.palette.text.primary,
  borderRadius: 100,
  backgroundColor: theme.palette.action.selected,
  padding: '0px 4px',
}));

export const PyPublicLabel = styled(Typography)(({ theme }) => ({
  fontSize: 12,
  fontWeight: 400,
  color: theme.palette.info.contrastText,
  borderRadius: 100,
  backgroundColor: theme.palette.info.main,
  padding: '0px 4px',
}));

export const PyCommentTimestamp = styled(Box)(() => ({
  display: 'flex',
  alignItems: 'center',
  gap: 4,
}));

export const PyCommentTimestampText = styled(Typography)(({ theme }) => ({
  color: theme.palette.text.secondary,
  fontSize: 12,
}));

export const PyCommentContent = styled(Box)({});

export const PyCommentText = styled(Comment.Body)(({ theme }) => ({
  color: theme.palette.text.primary,
  fontSize: 16,
  fontFamily: 'Gellix',
}));

const StyledMentionSpan = styled('span')({
  textDecoration: 'underline',
  textDecorationColor: 'currentColor',
  textUnderlineOffset: '2px',
});

export const PyCommentMention: React.ComponentType<CommentBodyMentionProps> = ({ mention, ...props }) => {
  const displayName = extractNameFromMentionId(mention.id);
  return <StyledMentionSpan {...props}>@{displayName}</StyledMentionSpan>;
};

const StyledEditorMentionSpan = styled('span')<{ isSelected?: boolean }>(({ isSelected }) => ({
  textDecoration: 'underline',
  textDecorationColor: 'currentColor',
  textUnderlineOffset: '2px',
  backgroundColor: isSelected ? 'rgba(0, 0, 0, 0.05)' : 'transparent',
}));

export const PyComposerMention: React.ComponentType<ComposerEditorMentionProps> = ({ isSelected, mention, ...props }) => {
  const displayName = extractNameFromMentionId(mention.id);
  return (
    <StyledEditorMentionSpan isSelected={isSelected} {...props}>
      @{displayName}
    </StyledEditorMentionSpan>
  );
};

export const PyComposerSuggestions = styled(Composer.Suggestions)({
  display: 'flex',
  width: '220px',
  flexDirection: 'column',
  alignItems: 'flex-start',
  background: 'white',
  borderRadius: 8,
  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1), 0 0 0 1px rgba(0, 0, 0, 0.05)',
  border: '1px solid rgba(0, 0, 0, 0.08)',
  maxHeight: '300px',
  overflow: 'hidden',
  zIndex: THREAD_INPUT_MENTION_SUGGESTIONS_Z_INDEX,
});

export const PyComposerSuggestionsList = styled(Composer.SuggestionsList)({
  display: 'flex',
  flexDirection: 'column',
  width: '100%',
  padding: 0,
  margin: 0,
  listStyle: 'none',
  overflowY: 'auto',
  maxHeight: '300px',
});

export const PyComposerSuggestionsListItem = styled(Composer.SuggestionsListItem)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  alignItems: 'flex-start',
  alignSelf: 'stretch',
  padding: '10px 16px',
  borderRadius: 0,
  cursor: 'pointer',
  border: 'none',
  background: 'transparent',
  transition: 'background-color 0.1s ease',
  color: theme.palette.text.primary,
  fontFamily: 'Gellix',
  fontSize: '16px',
  fontWeight: 600,

  '&:hover': {
    background: theme.palette.action.hover,
  },
}));

export const PyThreadActions = styled(Box)({
  display: 'flex',
  gap: 8,
});

export const PyThreadActionButton = styled(Button)(({ theme }) => ({
  color: theme.palette.text.primary,
  fontSize: 13,
  fontWeight: 600,
  padding: 0,
}));

export const PyThreadHeaderLeftSection = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  width: '234px',
});

export const PyThreadHeaderRightSection = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: 8,
});

export const PyThreadExpandIconButton = styled(IconButton)({
  padding: 0,
});

export const PyThreadDeleteIconButton = styled(IconButton)({
  padding: 0,
});

export const PyThreadMenuIconButton = styled(IconButton)({
  padding: 0,
});

export const PyThreadCollapseContainer = styled(Box)<{ isActive: boolean }>(({ isActive }) => ({
  width: '100%',
  paddingBottom: isActive ? '14px' : 0,
}));

export const PyThreadRepliesContainer = styled(Box)({
  paddingLeft: '12px',
  paddingTop: '12px',
});

export const PyThreadReplyIcon = styled(UndoRounded)({
  fontSize: '14px',
});

export const PyThreadExpandReplyIcon = styled(ExpandLess)({
  fontSize: '18px',
});

export const PyThreadCollapseReplyIcon = styled(ExpandMore)({
  fontSize: '18px',
});

export const PyThreadSecondaryIcon = styled(Box)(({ theme }) => ({
  color: theme.palette.text.secondary,
}));

export const PyCommentHeaderLeftSection = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: 8,
});

export const PyCommentMenuIconButton = styled(IconButton)({
  width: 24,
  height: 24,
});

export const PyCommentMenuIcon = styled(MoreHorizRounded)(({ theme }) => ({
  width: 24,
  height: 24,
  color: theme.palette.text.secondary,
}));

export const PyCommentTimeIcon = styled(AccessTimeRounded)(({ theme }) => ({
  width: 14,
  height: 14,
  color: theme.palette.text.secondary,
}));

export const PyCommentDeleteOverlay = styled(Box)({
  position: 'absolute',
  top: 0,
  left: '-10px',
  right: 0,
  backgroundColor: '#E6EAEB',
  padding: '16px',
  borderRadius: '24px',
  zIndex: DELETE_OVERLAY_Z_INDEX,
});

export const PyCommentDeleteText = styled(Typography)({
  fontSize: '15px',
  fontWeight: 700,
});

export const PyCommentDeleteActionsContainer = styled(Box)({
  display: 'flex',
  justifyContent: 'flex-end',
  gap: 8,
  marginTop: '6px',
});

export const PyCommentDeleteSubmitButton = styled(Button)(({ theme }) => ({
  backgroundColor: theme.palette.primary.main,
  color: 'white',
  '&:hover': {
    backgroundColor: theme.palette.primary.main,
  },
}));
